import { supabase } from '../config/supabase'

/**
 * Obtiene el wedding_id del usuario (considerando si es partner)
 */
const getUserWeddingId = async (userId) => {
  try {
    // Primero, obtener el perfil del usuario para verificar si es partner
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('partner_user_id')
      .eq('id', userId)
      .maybeSingle()

    if (profileError && profileError.code !== 'PGRST116') {
      console.error('Error fetching profile:', profileError)
    }

    // Determinar el user_id de la boda:
    // - Si el usuario tiene un partner_user_id (es partner), buscar la boda del usuario principal (partner_user_id)
    // - Si el usuario NO tiene partner_user_id pero hay otro perfil que tiene este usuario como partner_user_id,
    //   buscar la boda del usuario mismo (es el usuario principal)
    // - Si no tiene partner, buscar la boda del usuario mismo
    let weddingOwnerId = userId
    
    if (profile?.partner_user_id) {
      // Este usuario es un partner, buscar la boda del usuario principal
      weddingOwnerId = profile.partner_user_id
    } else {
      // Verificar si hay otro perfil que tenga este usuario como partner
      // Si es así, este usuario es el principal y debe buscar su propia boda
      const { data: partnerProfile } = await supabase
        .from('profiles')
        .select('id')
        .eq('partner_user_id', userId)
        .maybeSingle()
      
      // Si no hay partner que apunte a este usuario, buscar su propia boda
      weddingOwnerId = userId
    }

    const { data, error } = await supabase
      .from('weddings')
      .select('id')
      .eq('user_id', weddingOwnerId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle() // Usar maybeSingle() en lugar de single() para evitar 406

    if (error) {
      if (error.code === 'PGRST116') {
        return null
      }
      console.error('Error fetching wedding ID:', error)
      return null
    }

    return data?.id || null
  } catch (error) {
    console.error('Error fetching wedding ID:', error)
    return null
  }
}

/**
 * Genera un slug único para el formulario
 */
const generateFormSlug = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let slug = ''
  for (let i = 0; i < 16; i++) {
    slug += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return slug
}

/**
 * Obtiene o crea el formulario del Lifesaver Link para el usuario
 * @param {string} userId - ID del usuario
 * @returns {Promise<Object|null>}
 */
export const getOrCreateLifesaverForm = async (userId) => {
  try {
    const weddingId = await getUserWeddingId(userId)
    if (!weddingId) {
      throw new Error('No wedding found for user. Please create a wedding first.')
    }

    // Intentar obtener el formulario existente (solo uno por boda según el esquema)
    const { data: existingForm, error: fetchError } = await supabase
      .from('guest_forms')
      .select('*')
      .eq('wedding_id', weddingId)
      .maybeSingle()

    if (fetchError) {
      // Si el error es que la tabla no existe o no hay permisos
      if (fetchError.code === '42P01' || fetchError.message?.includes('relation') || fetchError.message?.includes('does not exist')) {
        throw new Error('The guest_forms table does not exist. Please create it in your Supabase database.')
      }
      // Si el error es de permisos RLS
      if (fetchError.code === '42501' || fetchError.message?.includes('permission')) {
        throw new Error('Permission denied. Please check your RLS policies for the guest_forms table.')
      }
      // Si no es un error de "no encontrado", lanzar el error
      if (fetchError.code !== 'PGRST116') {
        console.error('Fetch error:', fetchError)
        throw fetchError
      }
    }

    // Si existe, retornarlo
    if (existingForm) {
      return existingForm
    }

    // Si no existe, crear uno nuevo con campos básicos habilitados
    // First Name, Last Name y Email SIEMPRE deben estar presentes
    const formSlug = generateFormSlug()
    const { data: newForm, error: createError } = await supabase
      .from('guest_forms')
      .insert([
        {
          wedding_id: weddingId,
          form_slug: formSlug,
          enabled: true,
          collect_household: false,
          form_fields: [
            { name: 'first_name', label: 'First Name', required: true, type: 'text' },
            { name: 'last_name', label: 'Last Name', required: true, type: 'text' },
            { name: 'email', label: 'Email', required: true, type: 'email' },
          ],
        },
      ])
      .select()
      .single()

    if (createError) {
      console.error('Create error:', createError)
      // Si el error es que la tabla no existe
      if (createError.code === '42P01' || createError.message?.includes('relation') || createError.message?.includes('does not exist')) {
        throw new Error('The guest_forms table does not exist. Please create it in your Supabase database.')
      }
      // Si el error es de permisos RLS
      if (createError.code === '42501' || createError.message?.includes('permission')) {
        throw new Error('Permission denied. Please check your RLS policies for the guest_forms table.')
      }
      throw createError
    }

    return newForm
  } catch (error) {
    console.error('Error getting or creating lifesaver form:', error)
    throw error
  }
}

/**
 * Actualiza la configuración del formulario
 * @param {string} formId - ID del formulario
 * @param {Object} updates - Campos a actualizar
 * @returns {Promise<Object|null>}
 */
export const updateLifesaverForm = async (formId, updates) => {
  try {
    // Primero verificar que el formulario existe y es accesible
    const { data: existingForm, error: checkError } = await supabase
      .from('guest_forms')
      .select('id')
      .eq('id', formId)
      .maybeSingle()

    if (checkError) {
      console.error('Error checking form existence:', checkError)
      throw checkError
    }

    if (!existingForm) {
      throw new Error('Form not found. Please refresh the page and try again.')
    }

    // Logging para diagnóstico (siempre mostrar, no solo en development)
    console.log('🔵 updateLifesaverForm - INICIANDO UPDATE')
    console.log('🔵 formId:', formId)
    console.log('🔵 updates:', JSON.stringify(updates, null, 2))
    console.log('🔵 updates.form_fields length:', updates.form_fields?.length)

    // Hacer el UPDATE SIN .select() para evitar el error 406
    // El problema es que cuando usamos .select().maybeSingle() después de UPDATE,
    // Supabase puede devolver 406 si no hay filas afectadas o hay problemas de RLS
    console.log('🔵 Ejecutando UPDATE...')
    const { error: updateError } = await supabase
      .from('guest_forms')
      .update(updates)
      .eq('id', formId)

    if (updateError) {
      console.error('❌ Error updating lifesaver form:', updateError)
      console.error('❌ Error details:', {
        code: updateError.code,
        message: updateError.message,
        details: updateError.details,
        hint: updateError.hint,
      })
      
      // Si es un error de permisos RLS
      if (updateError.code === '42501' || updateError.message?.includes('permission') || updateError.message?.includes('policy')) {
        throw new Error('Permission denied. Row-level security policies are blocking the update. Please check your RLS policies.')
      }
      
      throw updateError
    }

    console.log('✅ UPDATE exitoso, esperando 200ms...')

    // Después del UPDATE exitoso, hacer un SELECT separado para obtener los datos actualizados
    // Esperar un pequeño momento para asegurar que el UPDATE se haya completado
    await new Promise(resolve => setTimeout(resolve, 200))

    console.log('🔵 Ejecutando SELECT para obtener datos actualizados...')
    const { data, error: selectError } = await supabase
      .from('guest_forms')
      .select('*')
      .eq('id', formId)
      .maybeSingle()

    if (selectError) {
      console.error('❌ Error fetching updated form:', selectError)
      throw new Error('Form was updated but could not be retrieved. Please refresh the page.')
    }

    if (!data) {
      console.error('❌ No se encontró el formulario después del UPDATE')
      throw new Error('Form not found after update. This may be a permissions issue. Please check your RLS policies.')
    }

    console.log('✅ SELECT exitoso, datos obtenidos:')
    console.log('✅ data.form_fields length:', data.form_fields?.length)
    console.log('✅ data.enabled:', data.enabled)
    console.log('✅ data.collect_household:', data.collect_household)
    console.log('✅ data completo:', data)

    return data
  } catch (error) {
    console.error('Error updating lifesaver form:', error)
    throw error
  }
}

/**
 * Obtiene el formulario por slug (para la página pública)
 * @param {string} slug - Slug del formulario
 * @returns {Promise<Object|null>}
 */
export const getFormByToken = async (slug) => {
  try {
    if (!slug || slug.trim() === '') {
      if (process.env.NODE_ENV === 'development') {
        console.warn('getFormByToken: Empty slug provided')
      }
      return null
    }
    
    // IMPORTANTE: Obtener el formulario SIN JOIN para evitar problemas de RLS
    // El JOIN con weddings puede fallar si las políticas RLS bloquean el acceso
    const { data: formData, error: formError } = await supabase
      .from('guest_forms')
      .select('*')  // Solo campos de guest_forms, SIN JOIN
      .eq('form_slug', slug.trim())
      .maybeSingle()  // Usar maybeSingle en lugar de single para evitar 406

    if (formError) {
      if (process.env.NODE_ENV === 'development') {
        console.error('getFormByToken: Error fetching form:', formError)
        console.error('Error code:', formError.code)
        console.error('Error message:', formError.message)
      }
      
      if (formError.code === 'PGRST116' || formError.code === '42P01') {
        return null
      }
      
      // Si es un error de permisos RLS, loguear pero continuar
      if (formError.code === '42501' || formError.message?.includes('permission') || formError.message?.includes('policy')) {
        if (process.env.NODE_ENV === 'development') {
          console.error('getFormByToken: RLS Policy Error - Public access may not be configured for guest_forms table')
        }
      }
      
      return null
    }

    if (!formData) {
      return null
    }

    // Intentar obtener datos de wedding si es necesario (opcional, puede fallar por RLS)
    try {
      if (formData.wedding_id) {
        const { data: weddingData } = await supabase
          .from('weddings')
          .select('id, user_id, name')
          .eq('id', formData.wedding_id)
          .maybeSingle()

        if (weddingData) {
          formData.weddings = weddingData
          
          // Intentar obtener perfil (puede fallar por RLS si no hay políticas públicas)
          if (weddingData.user_id) {
            console.log('🔍 Intentando obtener perfil para user_id:', weddingData.user_id)
            const { data: profileData, error: profileError } = await supabase
              .from('profiles')
              .select('first_name, last_name, partner_first_name, partner_last_name, partner_user_id')
              .eq('id', weddingData.user_id)
              .maybeSingle()

            if (profileError) {
              console.error('❌ Error obteniendo perfil:', profileError)
              console.error('❌ Error code:', profileError.code)
              console.error('❌ Error message:', profileError.message)
              console.warn('⚠️ Esto puede ser un problema de políticas RLS. Verifica que las políticas públicas estén configuradas.')
            }

            if (profileData) {
              console.log('✅ Perfil obtenido:', {
                first_name: profileData.first_name,
                partner_first_name: profileData.partner_first_name,
                partner_user_id: profileData.partner_user_id
              })
              // Si el perfil tiene partner_user_id, también obtener el perfil del partner
              if (profileData.partner_user_id) {
                const { data: partnerProfileData } = await supabase
                  .from('profiles')
                  .select('first_name, last_name')
                  .eq('id', profileData.partner_user_id)
                  .maybeSingle()
                
                if (partnerProfileData && partnerProfileData.first_name) {
                  // Si el partner tiene first_name, usar ese en lugar de partner_first_name
                  profileData.partner_first_name = partnerProfileData.first_name
                  profileData.partner_last_name = partnerProfileData.last_name
                }
              } else if (!profileData.partner_first_name) {
                // Si no hay partner_user_id pero tampoco hay partner_first_name,
                // intentar buscar si hay otro perfil que tenga este usuario como partner
                const { data: partnerProfileData } = await supabase
                  .from('profiles')
                  .select('first_name, last_name')
                  .eq('partner_user_id', weddingData.user_id)
                  .maybeSingle()
                
                if (partnerProfileData && partnerProfileData.first_name) {
                  profileData.partner_first_name = partnerProfileData.first_name
                  profileData.partner_last_name = partnerProfileData.last_name
                }
              }
              
              // Log para diagnóstico (siempre mostrar)
              console.log('🔍 getFormByToken - profileData completo:', profileData)
              console.log('🔍 getFormByToken - first_name:', profileData.first_name)
              console.log('🔍 getFormByToken - partner_first_name:', profileData.partner_first_name)
              console.log('🔍 getFormByToken - partner_user_id:', profileData.partner_user_id)
              
              // Si aún no tenemos partner_first_name pero tenemos partner_user_id, intentar obtenerlo de nuevo
              if (!profileData.partner_first_name && profileData.partner_user_id) {
                console.log('⚠️ No se encontró partner_first_name, intentando obtener perfil del partner directamente...')
                try {
                  const { data: partnerProfileData } = await supabase
                    .from('profiles')
                    .select('first_name, last_name')
                    .eq('id', profileData.partner_user_id)
                    .maybeSingle()
                  
                  if (partnerProfileData && partnerProfileData.first_name) {
                    console.log('✅ Perfil del partner obtenido:', partnerProfileData)
                    profileData.partner_first_name = partnerProfileData.first_name
                    profileData.partner_last_name = partnerProfileData.last_name
                  } else {
                    console.warn('⚠️ No se pudo obtener el perfil del partner')
                  }
                } catch (partnerError) {
                  console.error('❌ Error obteniendo perfil del partner:', partnerError)
                }
              }
              
              formData.weddings.profiles = profileData
            }
          }
        }
      }
    } catch (weddingError) {
      // No es crítico si no podemos obtener datos de wedding
      console.warn('getFormByToken: Could not fetch wedding data (may be RLS restriction):', weddingError)
      console.warn('getFormByToken: Error details:', {
        message: weddingError.message,
        code: weddingError.code,
        details: weddingError.details
      })
    }
    
    // Log final para diagnóstico
    if (formData.weddings && formData.weddings.profiles) {
      console.log('✅ getFormByToken: Perfiles obtenidos correctamente')
    } else {
      console.warn('⚠️ getFormByToken: No se pudieron obtener los perfiles. Verificar políticas RLS públicas.')
    }

    return formData
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('getFormByToken: Unexpected error:', error)
    }
    return null
  }
}

/**
 * Envía una respuesta del formulario y crea el invitado automáticamente
 * @param {string} slug - Slug del formulario
 * @param {Object} formData - Datos del formulario
 * @returns {Promise<Object>}
 */
export const submitLifesaverForm = async (slug, formData) => {
  try {
    // Obtener el formulario y el wedding_id
    const form = await getFormByToken(slug)
    if (!form) {
      throw new Error('Form not found or disabled')
    }

    const weddingId = form.wedding_id

    // Validar que los campos requeridos no estén vacíos
    if (!formData.first_name || !formData.first_name.trim()) {
      throw new Error('First name is required')
    }
    if (!formData.last_name || !formData.last_name.trim()) {
      throw new Error('Last name is required')
    }
    if (!formData.email || !formData.email.trim()) {
      throw new Error('Email is required')
    }

    // Validar solo los campos que están marcados como requeridos
    const enabledFields = form.form_fields || []
    for (const field of enabledFields) {
      if (field.name === 'plus_one') continue
      
      // Solo validar campos que están explícitamente marcados como requeridos
      // O los campos básicos que siempre son requeridos (first_name, last_name, email)
      const isRequired = field.required === true || 
                        field.name === 'first_name' || 
                        field.name === 'last_name' || 
                        field.name === 'email'
      
      if (isRequired) {
        const value = formData[field.name]
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          const fieldLabel = field.label || field.name
          throw new Error(`${fieldLabel} is required`)
        }
      }
    }

    // Crear el invitado principal solo si tiene datos válidos
    const fullName = `${formData.first_name.trim()} ${formData.last_name.trim()}`.trim()
    if (!fullName) {
      throw new Error('Name cannot be empty')
    }

    // Determinar si hay plus one antes de crear el invitado principal
    // Asegurar que has_plus_one sea un booleano válido
    const hasPlusOneValue = formData.has_plus_one
    const hasPlusOne = (hasPlusOneValue === true || hasPlusOneValue === 'true' || hasPlusOneValue === 1) && 
                       formData.plus_one_first_name?.trim() && 
                       formData.plus_one_last_name?.trim()

    // Construir guestData solo con campos válidos y conocidos
    // NO incluir campos dinámicos que puedan tener valores inválidos
    const guestData = {
      wedding_id: weddingId,
      first_name: formData.first_name.trim(),
      last_name: formData.last_name.trim(),
      full_name: fullName,
      email: formData.email?.trim() || null,
      phone: formData.phone?.trim() || null,
      country: formData.country?.trim() || null,
      notes: formData.address?.trim() ? `Address: ${formData.address.trim()}` : null,
      rsvp_status: 'pending',
      has_plus_one: Boolean(hasPlusOne), // Asegurar que sea un booleano explícito
    }
    
    // Log para diagnóstico
    console.log('🔍 submitLifesaverForm - guestData antes de insertar:', JSON.stringify(guestData, null, 2))
    console.log('🔍 submitLifesaverForm - has_plus_one type:', typeof guestData.has_plus_one, 'value:', guestData.has_plus_one)

    const { data: mainGuest, error: guestError } = await supabase
      .from('guests')
      .insert([guestData])
      .select()
      .single()

    if (guestError) throw guestError

    // Si hay plus one, crear también ese invitado y establecer la relación
    let plusOneGuest = null
    if (hasPlusOne) {
      const plusOneFullName = `${formData.plus_one_first_name.trim()} ${formData.plus_one_last_name.trim()}`.trim()
      if (!plusOneFullName) {
        throw new Error('Plus one name cannot be empty')
      }

      const plusOneData = {
        wedding_id: weddingId,
        first_name: formData.plus_one_first_name.trim(),
        last_name: formData.plus_one_last_name.trim(),
        full_name: plusOneFullName,
        email: formData.plus_one_email?.trim() || null,
        phone: formData.plus_one_phone?.trim() || null,
        rsvp_status: 'pending',
        guest_of_id: mainGuest.id, // Establecer relación con el invitado principal
        has_plus_one: false, // El +1 no tiene su propio +1
      }

      const { data: plusOne, error: plusOneError } = await supabase
        .from('guests')
        .insert([plusOneData])
        .select()
        .single()

      if (plusOneError) {
        console.error('Error creating plus one guest:', plusOneError)
        // Si falla crear el +1, al menos el invitado principal ya está creado
        throw new Error('Failed to create plus one guest: ' + plusOneError.message)
      }
      
      plusOneGuest = plusOne
      console.log('✅ Plus one guest created:', plusOneGuest.id, 'for main guest:', mainGuest.id)
    }

    return {
      success: true,
      mainGuest,
      plusOneGuest,
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error submitting lifesaver form:', error)
    }
    throw error
  }
}
